/* Lightweight, native motion. No scroll listeners or scroll-jacking. */
(() => {
  document.documentElement.classList.add('opale-enhanced');
  const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  let revealObserver;
  function reveal(root = document) {
    if (!('IntersectionObserver' in window) || motion.matches || window.Shopify?.designMode) return;
    if (!revealObserver) revealObserver = new IntersectionObserver((entries) => {
      entries.forEach(({ target, isIntersecting }) => {
        if (!isIntersecting) return;
        target.classList.add('op-is-visible');
        target.classList.remove('op-reveal-pending');
        revealObserver.unobserve(target);
      });
      // Fast scrolls can pass a section between observer samples. Keep any
      // already-passed content visible for back-scrolling and full-page views.
      const passed = Array.from(document.querySelectorAll('.op-reveal-pending'))
        .filter(element => element.getBoundingClientRect().top < window.innerHeight);
      passed.forEach(element => {
        element.classList.remove('op-reveal-pending');
        element.classList.add('op-is-visible');
        revealObserver.unobserve(element);
      });
    }, { threshold: 0.08 });
    root.querySelectorAll('[data-op-reveal]:not(.op-is-visible)').forEach((element) => {
      element.classList.add('op-reveal-pending');
      revealObserver.observe(element);
    });
  }
  class OpaleHeader extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        this.querySelectorAll('details[open]').forEach((details) => {
          details.open = false;
          details.querySelector('summary')?.focus();
        });
      }, { signal });
      document.addEventListener('click', (event) => {
        if (this.contains(event.target)) return;
        this.querySelectorAll('details[open]').forEach((details) => { details.open = false; });
      }, { signal });
      this.querySelectorAll('details').forEach((details) => details.addEventListener('toggle', () => {
        if (!details.open) return;
        this.querySelectorAll('details[open]').forEach((other) => { if (other !== details && !other.contains(details) && !details.contains(other)) other.open = false; });
        if (details.classList.contains('op-search')) details.querySelector('input')?.focus();
      }, { signal }));
      this.observeHero();
      document.addEventListener('shopify:section:load', () => this.observeHero(), { signal });
      document.addEventListener('shopify:section:reorder', () => this.observeHero(), { signal });
    }
    observeHero() {
      this.observer?.disconnect(); this.sentinel?.remove();
      if (this.classList.contains('op-header--overlay') && 'IntersectionObserver' in window) {
        const hero = document.querySelector('[data-op-hero]');
        if (hero) {
          this.sentinel = document.createElement('span');
          this.sentinel.style.cssText = 'position:absolute;top:100px;left:0;width:1px;height:1px;pointer-events:none';
          this.sentinel.setAttribute('aria-hidden', 'true');
          hero.appendChild(this.sentinel);
          this.observer = new IntersectionObserver(([entry]) => this.classList.toggle('is-solid', !entry.isIntersecting), { rootMargin: '-80px 0px 0px 0px' });
          this.observer.observe(this.sentinel);
        } else this.classList.add('is-solid');
      }
    }
    disconnectedCallback() {
      this.controller?.abort(); this.observer?.disconnect(); this.sentinel?.remove();
    }
  }
  class OpaleFilm extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');
      this.button = this.querySelector('button');
      if (!this.video || !this.button) return;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.button.hidden = false;
      this.inView = false;
      this.userPaused = false;
      this.button.addEventListener('click', () => {
        if (this.video.paused) { this.userPaused = false; this.play(); }
        else { this.userPaused = true; this.video.pause(); }
      }, { signal });
      this.video.addEventListener('playing', () => {
        this.classList.add('is-playing'); this.button.textContent = 'Pause film'; this.button.setAttribute('aria-label','Pause brand film'); this.button.setAttribute('aria-pressed','true');
      }, { signal });
      this.video.addEventListener('pause', () => {
        this.button.textContent = 'Play film'; this.button.setAttribute('aria-label','Play brand film'); this.button.setAttribute('aria-pressed','false');
      }, { signal });
      this.video.addEventListener('error', () => { this.classList.remove('is-playing'); this.button.hidden = true; }, { signal });
      this.motionChange = () => { if (motion.matches) this.video.pause(); else this.maybePlay(); };
      motion.addEventListener('change', this.motionChange);
      document.addEventListener('visibilitychange', () => { if (document.hidden) this.video.pause(); else this.maybePlay(); }, { signal });
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(([entry]) => {
          this.inView = entry.isIntersecting;
          if (this.inView) this.maybePlay(); else this.video.pause();
        }, { threshold: 0.15 });
        this.observer.observe(this);
      }
    }
    load() {
      if (this.loaded) return;
      this.video.querySelectorAll('source[data-src]').forEach(source => { source.src = source.dataset.src; });
      this.loaded = true; this.video.load();
    }
    async play() {
      this.load();
      try { await this.video.play(); } catch { this.classList.remove('is-playing'); }
    }
    maybePlay() {
      if (this.inView && !document.hidden && !motion.matches && !this.userPaused && this.dataset.autoplay === 'true') this.play();
    }
    disconnectedCallback() {
      this.video?.pause(); this.observer?.disconnect(); this.controller?.abort();
      if (this.motionChange) motion.removeEventListener('change', this.motionChange);
    }
  }
  if (!customElements.get('opale-header')) customElements.define('opale-header', OpaleHeader);
  if (!customElements.get('opale-film')) customElements.define('opale-film', OpaleFilm);
  reveal();
  document.addEventListener('shopify:section:load', event => reveal(event.target));
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll('[data-op-reveal]').forEach(element => revealObserver?.unobserve(element));
  });
  motion.addEventListener('change', () => {
    revealObserver?.disconnect(); revealObserver = undefined;
    document.querySelectorAll('.op-reveal-pending').forEach(element => element.classList.remove('op-reveal-pending'));
    if (!motion.matches) reveal();
  });
  // Local preview diagnostics only; never runs on the hosted storefront.
  if (location.hostname === '127.0.0.1' && 'PerformanceObserver' in window) {
    let cls = 0;
    let blocking = 0;
    const supported = PerformanceObserver.supportedEntryTypes || [];
    document.documentElement.dataset.opaleSupported = supported.join(',');
    const summarizeResources = () => {
      const entries = performance.getEntriesByType('resource');
      const totals = { requests:entries.length, transfer:0, encoded:0, unmeasured:0, images:0, imageEncoded:0, scripts:0, scriptEncoded:0, videoRequests:0 };
      entries.forEach(entry => {
        totals.transfer += entry.transferSize || 0;
        totals.encoded += entry.encodedBodySize || 0;
        if (!entry.encodedBodySize) totals.unmeasured++;
        if (entry.initiatorType === 'img') { totals.images++; totals.imageEncoded += entry.encodedBodySize || 0; }
        if (entry.initiatorType === 'script') { totals.scripts++; totals.scriptEncoded += entry.encodedBodySize || 0; }
        if (/\.(mp4|webm)(\?|$)/i.test(entry.name)) totals.videoRequests++;
      });
      document.documentElement.dataset.opaleResources = JSON.stringify(totals);
    };
    try {
      new PerformanceObserver(summarizeResources).observe({type:'resource',buffered:true});
      if (supported.includes('longtask')) {
        document.documentElement.dataset.opaleBlocking = '0';
        new PerformanceObserver(list => {
        list.getEntries().forEach(entry => { blocking += Math.max(0, entry.duration - 50); });
        document.documentElement.dataset.opaleBlocking = blocking.toFixed(0);
      }).observe({type:'longtask',buffered:true});
      }
      if (supported.includes('layout-shift')) {
        document.documentElement.dataset.opaleCls = '0.0000';
        new PerformanceObserver(list => {
        list.getEntries().forEach(entry => { if (!entry.hadRecentInput) cls += entry.value; });
        document.documentElement.dataset.opaleCls = cls.toFixed(4);
      }).observe({type:'layout-shift',buffered:true});
      }
      if (supported.includes('largest-contentful-paint')) new PerformanceObserver(list => {
        const entries = list.getEntries();
        document.documentElement.dataset.opaleLcp = entries[entries.length - 1].startTime.toFixed(0);
      }).observe({type:'largest-contentful-paint',buffered:true});
    } catch { /* Older browsers still render the complete storefront. */ }
  }
})();
